// Based on https://github.com/yWorks/yfiles-for-html-demos/blob/master/demos/02-tutorial-custom-styles/20-custom-arrow/MySimpleArrow.js

import {
  BaseClass,
  GeneralPath,
  IArrow,
  IBoundsProvider,
  IEdge,
  IRenderContext,
  IVisualCreator,
  Point,
  Rect,
  SvgVisual,
} from '@ardoq/yfiles';
import { ArrowType } from '@ardoq/api-types';

const getTransformMatrix = (anchor: Point, direction: Point) =>
  `matrix(${direction.x} ${direction.y} ${direction.y} ${direction.x} ${anchor.x} ${anchor.y})`;

/**
 * An IArrow implementation that can render all arrow types supported by Ardoq.
 */
class AqArrow extends BaseClass(IArrow, IVisualCreator, IBoundsProvider) {
  override get cropAtPort(): boolean {
    return false;
  }
  anchor: Point;
  direction: Point;
  $arrowFigure: GeneralPath | null;
  arrowType: ArrowType;
  arrowSize: number;

  constructor(arrowType: ArrowType = ArrowType.DEFAULT, arrowSize = 2) {
    super();
    this.anchor = Point.ORIGIN;
    this.direction = Point.ORIGIN;
    this.$arrowFigure = null;
    this.arrowType = arrowType;
    this.arrowSize = arrowSize;
  }

  get arrowFigure() {
    return this.$arrowFigure;
  }

  set arrowFigure(value) {
    this.$arrowFigure = value;
  }

  /**
   * Typically returns the length of the arrow (the distance from the arrow's
   * tip to the position where the visual representation of the edge's path
   * should begin).
   *
   * Because Ardoq's arrow types are inconsistent with regards to size, shape
   * and precision, we simply place the arrow on top of the edge without hiding
   * the part of the edge which lays behind the arrow. We accomplish this by
   * returning a length of 0.
   */
  override get length() {
    return 0;
  }

  /**
   * Returns the distance from the node's edge port to where the edge begins.
   *
   * This value is used by IEdgeStyle implementations to let the edge appear to
   * end shortly before its actual target. We don't want that, so we return 0.
   */
  override get cropLength() {
    return 0;
  }

  getVisualCreator(
    _edge: IEdge,
    _atSource: boolean,
    anchor: Point,
    direction: Point
  ) {
    this.anchor = anchor;
    this.direction = direction;
    return this;
  }

  getBoundsProvider(
    _edge: IEdge,
    _atSource: boolean,
    anchor: Point,
    direction: Point
  ) {
    this.anchor = anchor;
    this.direction = direction;
    return this;
  }

  createVisual() {
    // create a new empty path where we can set marker-start to arrowType
    if (!this.arrowFigure) {
      this.arrowFigure = new GeneralPath();
      this.arrowFigure.moveTo(new Point(0, 0));
      this.arrowFigure.close();
    }

    const svgElement = this.arrowFigure.createSvgPath();
    svgElement.setAttribute('marker-start', `url(#${this.arrowType})`);
    svgElement.setAttribute(
      'transform',
      getTransformMatrix(this.anchor, this.direction)
    );
    svgElement.setAttribute('stroke-width', `${this.arrowSize}`);

    return new SvgVisual(svgElement);
  }

  updateVisual(_context: IRenderContext, oldVisual: SvgVisual) {
    const svgElement = oldVisual.svgElement;
    svgElement.setAttribute(
      'transform',
      getTransformMatrix(this.anchor, this.direction)
    );
    return oldVisual;
  }

  getBounds() {
    return new Rect(this.anchor.x - 8, this.anchor.y - 8, 32, 32);
  }
}

export { AqArrow };
